import ExcelJS from 'exceljs/dist/exceljs.bare';
import { saveAs } from 'file-saver';

import productCenterStore from '../../store/product-center.store';
import {
  IProductCenterStore,
  TableTypeEnum,
} from '../../store/product-center.store.model';
import { getSelectedProductsField } from '../../store/product-center.store.utils';
import {
  Product,
  Disclaimer,
  DisclaimerPositioning,
  View,
  FundReturnKey,
} from '../../product-center-table-wrapper/product-center-table-wrapper.model';
import { Constants as ProductCenterTableWrapperConstants } from '../../product-center-table-wrapper/product-center-table-wrapper.constants';
import { Constants } from './excel-service.constants';
import { CumulativeData, TabConfig } from './excel.service.model';

import { getPerformanceConfig } from './table-configs/performance.config';
import { getTileConfig } from './table-configs/cumulative-performance.config';
import { getPriceConfig } from './table-configs/price.config';
import {
  convertHtmlToRichText,
  formatCell,
  formatDate,
  getCumulativeWorksheetName,
} from './excel-service.utils';
import { getLatestCumulativeDataPoint } from '../../product-center-table-wrapper/product-center-table-wrapper.utils';
import { i18nService } from '../../../common/i18n/i18n';
import Locale from '../../../common/utils/locale';

class ExcelService {
  private readonly workbook: ExcelJS.Workbook;
  private worksheet?: ExcelJS.Worksheet;
  private productCenterState: IProductCenterStore =
    productCenterStore.getState();
  private selectedProducts: Product['productCode'][] = [];
  private filteredProducts: Product[] = [];
  private currentFundReturnKey?: FundReturnKey;
  private selectedTabInTableView?: number;
  private selectedView?: View;
  private selectedTabInTileView?: number;
  private currentConfig?: TabConfig;
  private localize;
  private readonly localeInstance: Locale;

  constructor(localeInstance?: Locale) {
    this.localeInstance = localeInstance ?? new Locale();
    this.workbook = new ExcelJS.Workbook();
    productCenterStore.subscribe(async (state) => {
      await this.handleStateUpdate(state);
    });
  }

  /**
   * Generates an Excel file based on the current configuration.
   * @returns A Promise that resolves when the Excel file is generated.
   */
  public async generateExcelFile(): Promise<void> {
    if (!i18nService.isI18nLoadedForLanguage(this.localeInstance.language)) {
      await new Promise((resolve) => {
        i18nService.loadi18nForLanguage(this.localeInstance.language, resolve);
      });
    }

    this.localize = i18nService.getFixedT(
      this.localeInstance.language,
      null,
      null,
    );

    await this.handleStateUpdate(this.productCenterState);
    this.worksheet = this.workbook.addWorksheet(
      this.currentConfig?.worksheetName,
    );

    this.buildWorksheet();

    try {
      const buffer = await this.workbook.xlsx.writeBuffer();
      const fileName =
        this.currentConfig?.fileName ||
        `${this.localize(Constants.TRANSLATION_KEYS.FILE_NAME.DEFAULT)}.${Constants.EXCEL_FILE_EXTENSION}`;
      saveAs(new Blob([buffer], { type: Constants.EXCEL_MIME_TYPE }), fileName);
    } catch (error) {
      console.warn('Failed to save the Excel file:', error);
    }
  }

  /**
   * Updates the state of the product center.
   *
   * @param state - The new state of the product center.
   */
  private async handleStateUpdate(state: IProductCenterStore): Promise<void> {
    this.productCenterState = state;
    this.filteredProducts = state.getFilteredProducts();
    this.selectedTabInTableView = state.selectedTabInTableView;
    this.selectedTabInTileView = state.performanceTileView.selectedTab;
    this.selectedView = state.selectedView;
    this.currentFundReturnKey = state.performanceTileView.currentFundReturnKey;
    this.currentConfig = await this.getTabConfig();
    this.selectedProducts =
      this.selectedView === View.TABLE
        ? state.selectedProducts[
            getSelectedProductsField(View.TABLE, this.selectedTabInTableView)
          ]
        : state.performanceTileView.selectedProducts[
            getSelectedProductsField(
              View.CHART_TILES,
              this.selectedTabInTileView,
            )
          ];
  }

  /**
   * Retrieves the tab configuration based on the selected tab in the table view.
   * @returns A promise that resolves to the tab configuration object.
   */
  private async getTabConfig(): Promise<TabConfig> {
    if (this.selectedView === View.TABLE) {
      const isPerformance =
        this.selectedTabInTableView ===
        ProductCenterTableWrapperConstants.TABLE_TAB_INDEX.PERFORMANCE;
      return isPerformance
        ? this.getPerformanceConfig()
        : this.getPriceConfig();
    } else {
      return this.getTileConfig();
    }
  }

  /**
   * Returns the configuration object for the performance tab.
   * @returns {Promise<TabConfig>} A promise that resolves to the configuration object for the performance tab.
   */
  private async getPerformanceConfig(): Promise<TabConfig> {
    return getPerformanceConfig(this.localeInstance.language);
  }

  /**
   * Returns the configuration object for the price tab.
   * @returns {Promise<TabConfig>} A promise that resolves to the configuration object for the price tab.
   */
  private async getPriceConfig(): Promise<TabConfig> {
    return getPriceConfig(this.baseCurrency, this.localeInstance.language);
  }

  /**
   * Returns the configuration object for the price tab.
   * @returns {Promise<TabConfig>} A promise that resolves to the configuration object for the Tile view.
   */
  private async getTileConfig(): Promise<TabConfig> {
    const worksheetName = await getCumulativeWorksheetName(
      this.selectedTabInTileView,
    );

    return getTileConfig(() => worksheetName, this.localeInstance.language);
  }

  /**
   * Retrieves the cumulative data for a given product.
   * @param product - The product for which to retrieve the cumulative data.
   * @returns The cumulative data object containing the month end date, fund value, benchmark value, and benchmark name.
   */
  private getFundReturnData(product: Product): CumulativeData {
    if (!product?.cumulativeChart?.data || !this.currentFundReturnKey) {
      return {
        monthEndDate: null,
        fundValue: null,
        benchmarkValue: null,
        benchmarkName: null,
      };
    }
    const fundReturnData =
      product.cumulativeChart?.data[this.currentFundReturnKey];
    const fundReturnBenchmarks = product.cumulativeChart?.benchmarks ?? [];
    const benchmarkName = fundReturnBenchmarks[0].name ?? null;
    const latestDataPoint = getLatestCumulativeDataPoint(
      fundReturnData ?? [],
      this.currentFundReturnKey,
    );

    return {
      monthEndDate: latestDataPoint?.asOfDate,
      fundValue: latestDataPoint?.fundValue,
      benchmarkValue: latestDataPoint?.benchmarkValues?.[0] ?? null,
      benchmarkName,
    };
  }

  private get baseCurrency(): string {
    const product = this.filteredProducts.find(
      (p) => p.productName?.baseCurrencyName,
    );
    return product?.productName?.baseCurrencyName || '';
  }

  /**
   * Builds the worksheet by applying base styling, adding header rows, adding data rows,
   * adding footer disclaimers, and setting column widths.
   */
  private buildWorksheet(): void {
    if (!this.worksheet || !this.currentConfig) {
      return;
    }

    this.applyBaseStyling();
    this.addHeaderRows();
    this.addDataRows();
    this.addFooterDisclaimers();
    this.setColumnWidths();
  }

  /**
   * Applies base styling to the worksheet.
   */
  private applyBaseStyling(): void {
    if (!this.worksheet) {
      return;
    }

    this.worksheet.eachRow((row) => {
      row.font = {
        name: Constants.FONT.TIMES_NEW_ROMAN,
        size: Constants.FONT.SIZE,
      };
    });
  }

  /**
   * Adds header rows to the worksheet.
   * If the worksheet or current configuration is not available, the function returns early.
   * The function generates common rows based on the current configuration and adds them to the worksheet.
   * If the selected products field is set to TableTypeEnum.PERFORMANCE, it adds a specific row with placeholders.
   * Finally, it adds a tab-specific header row with custom formatting.
   */
  private addHeaderRows(): void {
    if (!this.worksheet || !this.currentConfig) {
      return;
    }

    const cumulativeData = this.getFundReturnData(
      this.filteredProducts.filter((product) =>
        this.selectedProducts.includes(product.productCode),
      )[0],
    );
    let asOfDate = '';
    if (this.selectedView === View.TABLE) {
      asOfDate =
        this.productCenterState.originalProductCenterObject.products[0]
          ?.performance?.performanceAsOfDate ?? '';
    } else {
      asOfDate =
        cumulativeData.monthEndDate ??
        this.productCenterState.originalProductCenterObject.products[0]
          ?.performance?.performanceAsOfDate ??
        '';
    }

    const row9Values = {
      asOfDate: formatDate(asOfDate),
    };

    const commonRows = [
      [window.location.href || Constants.ROW_CONTENT.EMPTY],
      [
        this.localize(Constants.TRANSLATION_KEYS.ROW_CONTENT.ROW_2, {
          country: this.localeInstance?.getCountryName().toUpperCase() || '',
        }),
      ],
      ...Array(2).fill([Constants.ROW_CONTENT.EMPTY]),
      [
        this.localize(Constants.TRANSLATION_KEYS.ROW_CONTENT.ROW_5, {
          numberOfProductsShown: `${this.productCenterState.filteredProducts.filter((product) => this.selectedProducts.includes(product.productCode)).length}`,
          numberOfProducts: `${this.productCenterState.originalProductCenterObject.products.length}`,
        }),
      ],
      ...Array(2).fill([Constants.ROW_CONTENT.EMPTY]),
      [
        this.selectedView === View.TABLE
          ? this.currentConfig.worksheetName
          : this.localize(
              Constants.TRANSLATION_KEYS.ROW_CONTENT.ROW_8_CUMULATIVE,
            ),
      ],
      ...((this.selectedView === View.TABLE &&
        getSelectedProductsField(
          View.TABLE,
          this.selectedTabInTableView ?? 0,
        ) === TableTypeEnum.PERFORMANCE) ||
      this.selectedView === View.CHART_TILES
        ? [
            [
              '',
              '',
              '',
              '',
              '',
              this.localize(
                this.selectedView === View.TABLE
                  ? this.localize(
                      Constants.TRANSLATION_KEYS.ROW_CONTENT.ROW_9_PERFORMANCE,
                    )
                  : this.localize(
                      Constants.TRANSLATION_KEYS.ROW_CONTENT.ROW_9_CUMULATIVE,
                    ),
                row9Values,
              ),
            ],
          ]
        : [Constants.ROW_CONTENT.EMPTY]),
    ];

    commonRows.forEach((rowData) => this.addStyledRow(rowData));

    // Add tab-specific header
    const headerRow = this.worksheet.addRow(this.currentConfig.headers);
    if (
      this.selectedView === View.TABLE &&
      getSelectedProductsField(View.TABLE, this.selectedTabInTableView ?? 0) ===
        TableTypeEnum.PERFORMANCE
    ) {
      headerRow.height = 44;
      headerRow.alignment = {
        vertical: Constants.ALIGNMENT.VERTICAL.BOTTOM,
        wrapText: true,
      };
    }
    headerRow.font = {
      name: Constants.FONT.TIMES_NEW_ROMAN,
      size: Constants.FONT.SIZE,
    };
  }

  /**
   * Adds data rows to the worksheet based on the filtered products and selected products.
   * Each product's row data is obtained from the current configuration and added to the worksheet.
   * Cell formatters are applied to format specific cells, and special formatting is applied if provided.
   * Empty rows are added after the last product row based on acceptance criteria.
   */
  private addDataRows(): void {
    if (!this.worksheet || !this.currentConfig) {
      return;
    }

    const addRowWithFormatting = (rowData: any[]) => {
      const row = this.addStyledRow(rowData);
      this.currentConfig?.cellFormatters?.forEach(({ index, format }) => {
        formatCell(row.getCell(index + 1), rowData[index], format);
      });
      this.currentConfig?.specialFormatting?.(row);
    };

    this.filteredProducts
      .filter((product) => this.selectedProducts.includes(product.productCode))
      .forEach((product) => {
        const fundReturnData = this.getFundReturnData(product);
        const rowData = this.currentConfig?.getRowData(product, fundReturnData);
        if (rowData) {
          addRowWithFormatting(rowData);

          if (
            this.selectedView === View.CHART_TILES &&
            this.currentConfig?.getBenchmarkRowData
          ) {
            const benchmarkRowData =
              this.currentConfig.getBenchmarkRowData(fundReturnData) ?? [];
            addRowWithFormatting(benchmarkRowData);
          }
        }
      });

    // Add empty rows after the last product row based on acceptance criteria
    for (let i = 0; i < Constants.EMPTY_ROWS_AFTER_PRODUCTS; i++) {
      this.worksheet.addRow([Constants.ROW_CONTENT.EMPTY]);
    }
  }

  /**
   * Adds footer disclaimers to the worksheet.
   *
   * @remarks
   * This method retrieves the footer disclaimers from the original product center object
   * and adds them to the worksheet as styled rows with rich text content.
   *
   * @private
   * @returns void
   */
  private addFooterDisclaimers(): void {
    if (!this.worksheet || !this.currentConfig?.renderFooterDisclaimers) {
      return;
    }

    const footerDisclaimers =
      this.productCenterState.originalProductCenterObject.disclaimers?.filter(
        (d: Disclaimer) => d.positioning === DisclaimerPositioning.FOOTER,
      ) || [];

    footerDisclaimers.forEach((disclaimer) => {
      disclaimer.disclaimerText.html
        .split(/<\/p>/i)
        .filter((p) => p.trim())
        .forEach((paragraph) => {
          this.addStyledRow([Constants.ROW_CONTENT.EMPTY]);
          const row = this.addStyledRow([Constants.ROW_CONTENT.EMPTY]);
          const cell = row.getCell(1);
          cell.value = {
            richText: convertHtmlToRichText(
              paragraph.replace(/\n/g, '') +
                Constants.HTML_TAGS.PARAGRAPH.CLOSE,
            ),
          };
          cell.alignment = { wrapText: false };
        });
    });
  }

  /**
   * Sets the column widths of the worksheet based on the current configuration.
   * If the worksheet or current configuration is not available, the function returns early.
   */
  private setColumnWidths(): void {
    if (!this.worksheet || !this.currentConfig) {
      return;
    }

    this.worksheet.columns = this.currentConfig.columnWidths.map((width) => ({
      width,
      style: {
        font: {
          name: Constants.FONT.TIMES_NEW_ROMAN,
          size: Constants.FONT.SIZE,
        },
      },
    }));
  }

  /**
   * Adds a styled row to the worksheet.
   *
   * @param data - The data to be added to the row.
   * @returns The added row.
   */
  private addStyledRow(data: any[]): ExcelJS.Row {
    if (!this.worksheet) {
      throw new Error('Worksheet is not defined');
    }

    const row = this.worksheet.addRow(data);
    row.font = {
      name: Constants.FONT.TIMES_NEW_ROMAN,
      size: Constants.FONT.SIZE,
    };
    return row;
  }
}

export default ExcelService;
